import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import type { WikiAnswer } from "../types/wiki-qa.types";

export const WIKI_QA_STORAGE_KEY = "battsupport.wiki-qa";

interface WikiQaState {
	isOpen: boolean;
	/** Newest first. Each entry is a self-contained question + answer. */
	entries: Array<WikiAnswer>;
	open: () => void;
	close: () => void;
	toggle: () => void;
	addEntry: (entry: WikiAnswer) => void;
	clear: () => void;
}

/**
 * The only persisted store in the app. History must outlive a page reload but
 * die with the session, so this uses sessionStorage — localStorage would carry
 * one agent's questions into the next agent's shift on a shared workstation.
 *
 * `isOpen` is deliberately not persisted: a reload must not reopen the drawer
 * on top of the workspace.
 */
export const useWikiQaStore = create<WikiQaState>()(
	persist(
		(set) => ({
			isOpen: false,
			entries: [],
			open: () => set({ isOpen: true }),
			close: () => set({ isOpen: false }),
			toggle: () => set((state) => ({ isOpen: !state.isOpen })),
			addEntry: (entry) =>
				set((state) => ({ entries: [entry, ...state.entries] })),
			clear: () => set({ isOpen: false, entries: [] }),
		}),
		{
			name: WIKI_QA_STORAGE_KEY,
			storage: createJSONStorage(() => sessionStorage),
			partialize: (state) => ({ entries: state.entries }),
		}
	)
);
